import { FeatureFlagId } from '@craft/feature-flags';
import { titleCase, type TitleCase } from '@craft/lib/formatters';
import { ConditionallyRender, type ConditionallyRenderProps } from '@craft/ui/components/util';

import {
  type FeatureFlagDesignation,
  useHasFeatureFlag,
} from '@/src/hooks/featureFlags/useHasFeatureFlag';

interface HasFeatureFlagProps extends Omit<ConditionallyRenderProps, 'show'> {
  readonly flag: FeatureFlagDesignation;
  readonly isInverted?: boolean;
}

const LocalHasFeatureFlag: React.FC<HasFeatureFlagProps> = ({
  children,
  flag,
  isInverted,
  ...props
}) => {
  const hasFeatureFlag = useHasFeatureFlag(flag, { isInverted });
  return (
    <ConditionallyRender {...props} show={hasFeatureFlag}>
      {children}
    </ConditionallyRender>
  );
};

type HasFeatureFlagPartials = {
  [key in FeatureFlagId as TitleCase<key, true>]: React.FC<Omit<HasFeatureFlagProps, 'flag'>>;
};

/**
 * A component that can be used to conditionally render content only in the case that the user
 * has the specified feature flag, {@link FeatureFlagId}.
 *
 * The component can either be used directly or via any one of its partials, each of which is
 * associated with a specific feature flag, {@link FeatureFlagId}.
 *
 * @see {@link FeatureFlagId}
 *
 * @example
 * // Using the component directly.
 * <HasFeatureFlag flag={FeatureFlagId.MmfCredentialing}>
 *   <MmfCredentialingContent />
 * </HasFeatureFlag>
 * @example
 * // Using the component via a partial.
 * <HasFeatureFlag.MmfCredentialing>
 *   <MmfCredentialingContent />
 * </HasFeatureFlag.MmfCredentialing>
 */
export const HasFeatureFlag = Object.assign(LocalHasFeatureFlag, {
  ...Object.values(FeatureFlagId).reduce(
    (prev, value) => ({
      ...prev,
      [titleCase(value, true)]: ({ children, ...props }: Omit<HasFeatureFlagProps, 'flag'>) => (
        <LocalHasFeatureFlag {...props} flag={value}>
          {children}
        </LocalHasFeatureFlag>
      ),
    }),
    {} as HasFeatureFlagPartials,
  ),
});
